import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, ClipboardCheck } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";

const STATUS_LABELS = {
  awaiting_questionnaire: "Waiting on customer's questionnaire",
  submitted: "Questionnaire submitted",
  in_progress: "In progress",
  awaiting_approval: "Waiting on customer approval",
  approved: "Customer approved",
  changes_requested: "Customer requested changes",
  completed: "Completed",
};

const PROFILE_FIELDS = [
  { value: "about_you", label: "About you" },
  { value: "care_experience_description", label: "Care experience description" },
  { value: "professional_background", label: "Professional background" },
  { value: "testimonials", label: "Testimonials" },
];

export default function ServiceOrderPanel({ order, questionnaire, onUpdated }) {
  const [draftText, setDraftText] = useState(order.draft_wording || "");
  const [notes, setNotes] = useState(order.admin_notes || "");
  const [completeNote, setCompleteNote] = useState("");
  const [profileField, setProfileField] = useState("about_you");
  const [busy, setBusy] = useState(null);

  const call = async (key, fn, successMsg) => {
    setBusy(key);
    try {
      const { data } = await fn();
      const patch = { ...data };
      if (patch.applied_field) patch.applied_to_profile_field = patch.applied_field;
      onUpdated(patch);
      if (successMsg) toast.success(successMsg);
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
      return null;
    } finally {
      setBusy(null);
    }
  };

  const answerLabel = (id) => (questionnaire || []).find((q) => q.id === id)?.label || id;

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4" data-testid={`admin-shop-service-panel-${order.id}`}>
      <div className="flex items-center justify-between">
        <span className="pill bg-purple/10 text-purple" data-testid={`admin-shop-service-status-${order.id}`}>
          {STATUS_LABELS[order.service_status] || order.service_status}
        </span>
        {order.service_status === "submitted" && (
          <button
            disabled={busy === "start"}
            onClick={() => call("start", () => api.post(`/api/admin/shop/orders/${order.id}/service-status`, { status: "in_progress" }))}
            className="btn-outline text-xs" data-testid={`admin-shop-start-work-${order.id}`}
          >
            {busy === "start" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Start work
          </button>
        )}
      </div>

      {Object.keys(order.questionnaire_answers || {}).length > 0 && (
        <div className="text-sm space-y-1.5" data-testid={`admin-shop-service-answers-${order.id}`}>
          <p className="font-medium text-ink">Customer's answers</p>
          {Object.entries(order.questionnaire_answers).map(([qid, ans]) => {
            const label = answerLabel(qid);
            const separator = /[?!.:]$/.test(label) ? "" : ":";
            return (
              <p key={qid} className="text-ink-muted"><span className="text-ink">{label}{separator}</span> {Array.isArray(ans) ? ans.join(", ") : ans}</p>
            );
          })}
        </div>
      )}

      {order.service_status === "changes_requested" && order.customer_approval_notes && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid={`admin-shop-changes-requested-${order.id}`}>
          Customer's note: {order.customer_approval_notes}
        </p>
      )}

      {["in_progress", "changes_requested"].includes(order.service_status) && (
        <div className="space-y-2">
          <label className="block text-sm font-medium">Draft wording to send for approval</label>
          <textarea rows={5} value={draftText} onChange={(e) => setDraftText(e.target.value)}
            data-testid={`admin-shop-draft-wording-${order.id}`}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          <button
            disabled={busy === "send" || !draftText.trim()}
            onClick={() => call("send", () => api.post(`/api/admin/shop/orders/${order.id}/send-draft-wording`, { text: draftText }))}
            className="btn-teal text-sm" data-testid={`admin-shop-send-draft-${order.id}`}
          >
            {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send for customer approval
          </button>
        </div>
      )}

      {order.service_status === "awaiting_approval" && (
        <p className="text-sm text-ink-muted" data-testid={`admin-shop-awaiting-approval-${order.id}`}>
          Draft sent — waiting for the customer to approve or request changes.
        </p>
      )}

      {order.service_status === "approved" && order.applies_to_carer_profile && (
        <div className="space-y-2" data-testid={`admin-shop-apply-profile-box-${order.id}`}>
          <p className="text-sm text-teal flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Customer approved the wording.</p>
          <label className="block text-sm font-medium">Add to which profile section?</label>
          <select value={profileField} onChange={(e) => setProfileField(e.target.value)}
            data-testid={`admin-shop-profile-field-${order.id}`} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            {PROFILE_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <button
            disabled={busy === "apply"}
            onClick={() => call("apply", () => api.post(`/api/admin/shop/orders/${order.id}/apply-to-profile`, { field: profileField }))}
            className="btn-teal text-sm ml-2" data-testid={`admin-shop-apply-to-profile-${order.id}`}
          >
            {busy === "apply" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />} Apply to their profile
          </button>
        </div>
      )}

      {order.service_status === "approved" && !order.applies_to_carer_profile && (
        <div className="space-y-2" data-testid={`admin-shop-mark-complete-box-${order.id}`}>
          <p className="text-sm text-teal flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Customer approved the work.</p>
          <input value={completeNote} onChange={(e) => setCompleteNote(e.target.value)}
            placeholder="Optional note to include in the completion email"
            data-testid={`admin-shop-complete-note-${order.id}`} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          <button
            disabled={busy === "complete"}
            onClick={() => call("complete", () => api.post(`/api/admin/shop/orders/${order.id}/mark-completed`, { note: completeNote }))}
            className="btn-teal text-sm" data-testid={`admin-shop-mark-completed-${order.id}`}
          >
            {busy === "complete" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Mark completed
          </button>
        </div>
      )}

      {order.service_status === "completed" && (
        <p className="text-sm text-teal" data-testid={`admin-shop-completed-note-${order.id}`}>
          Completed{order.applied_to_profile_field ? ` — added to "${PROFILE_FIELDS.find((f) => f.value === order.applied_to_profile_field)?.label || order.applied_to_profile_field}"` : ""}.
        </p>
      )}

      <div className="space-y-2 pt-2 border-t border-slate-200">
        <label className="block text-sm font-medium">Private admin notes</label>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
          data-testid={`admin-shop-notes-${order.id}`} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
        <button
          disabled={busy === "notes"}
          onClick={() => call("notes", () => api.post(`/api/admin/shop/orders/${order.id}/notes`, { notes }), "Notes saved.")}
          className="btn-outline text-xs" data-testid={`admin-shop-save-notes-${order.id}`}
        >
          {busy === "notes" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save notes
        </button>
      </div>
    </div>
  );
}
